/**
 * HOOKS SIMPLIFIÉS ET FONCTIONNELS POUR LE FILTRAGE PAR TAGS
 * ==========================================================
 * 
 * Version corrigée sans erreurs TypeScript
 */

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { tagsService } from '../services/tags'
import { tagKeys } from './useTags'
import type {
  EntityFilterRequest,
  FilteredEntitiesResponse
} from '../types/tags'


// =============================================
// QUERY KEYS SIMPLIFIÉS
// =============================================

export const entityTagsQueryKeys = {
  all: ['entity-tags'] as const,
  entity: (entityType: string, entityId: number) => 
    ['entity-tags', 'entity', entityType, entityId] as const,
  filteredEntities: (entityType: string, filter: EntityFilterRequest) =>
    ['entity-tags', 'filtered-entities', entityType, filter] as const,
  popularTags: (entityType: string, limit?: number) =>
    ['entity-tags', 'popular-tags', entityType, limit] as const,
  tagStats: (entityType: string) =>
    ['entity-tags', 'tag-stats', entityType] as const,
}

// =============================================
// HOOKS DE LECTURE D'ENTITÉS
// =============================================

/**
 * Hook pour récupérer les tags d'une entité
 */
export const useEntityTags = (entityType: string, entityId: number, enabled: boolean = true) => {
  return useQuery({
    queryKey: entityTagsQueryKeys.entity(entityType, entityId),
    queryFn: async () => {
      try {
        const tags = await tagsService.getEntityTags(entityType, entityId);
        return tags || [];
      } catch (error) {
        const status = (error as any)?.response?.status;
        if (status !== 404) {
          console.error('Error fetching entity tags:', error);
        }
        return [];
      }
    },
    enabled: enabled && !!entityType && !!entityId,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  })
}

// =============================================
// HOOKS DE FILTRAGE
// =============================================

/**
 * Hook générique pour filtrer les entités par tags
 */
export const useFilteredEntities = (
  entityType: string, 
  filter: EntityFilterRequest,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: entityTagsQueryKeys.filteredEntities(entityType, filter),
    queryFn: async (): Promise<FilteredEntitiesResponse> => {
      const result = await tagsService.filterEntitiesByTags(entityType, filter);
      return result;
    },
    enabled: enabled && !!entityType,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

/**
 * Hook spécialisé pour filtrer les contacts par tags
 */
export const useFilteredContacts = (
  filter: EntityFilterRequest,
  enabled: boolean = true
) => {
  return useFilteredEntities('contact', filter, enabled)
}

/**
 * Hook spécialisé pour filtrer les companies par tags
 */
export const useFilteredCompanies = (
  filter: EntityFilterRequest,
  enabled: boolean = true
) => {
  return useFilteredEntities('company', filter, enabled)
}

/**
 * Hook spécialisé pour filtrer les deals par tags
 */
export const useFilteredDeals = (
  filter: EntityFilterRequest,
  enabled: boolean = true
) => {
  return useFilteredEntities('deal', filter, enabled)
}

/**
 * Hook spécialisé pour filtrer les products par tags
 */
export const useFilteredProducts = (
  filter: EntityFilterRequest,
  enabled: boolean = true
) => {
  return useFilteredEntities('product', filter, enabled)
}

// =============================================
// HOOKS DE MUTATION SIMPLIFIÉS
// =============================================

/**
 * Hook pour mettre à jour les tags d'une entité
 */
export const useUpdateEntityTags = () => {
  const invalidateTagCaches = useInvalidateTagCaches()
  const queryClient = useQueryClient()

  return {
    mutateAsync: async ({ entityType, entityId, tagIds }: {
      entityType: string
      entityId: number
      tagIds: number[]
    }) => {
      console.log('🔄 Updating entity tags:', { entityType, entityId, tagIds });
      
      try {
        // Appel réel API pour mettre à jour les tags et récupérer la liste à jour
        const updated = await tagsService.updateEntityTags(entityType, entityId, tagIds)
        // Mettre à jour le cache immédiatement
        queryClient.setQueryData(entityTagsQueryKeys.entity(entityType, entityId), updated)
        
        // Invalider les caches
        invalidateTagCaches(entityType, entityId);
        
        return updated;
      } catch (error) {
        console.error('❌ Erreur lors de la mise à jour des tags:', error);
        throw error;
      }
    }
  };
}

// =============================================
// HOOKS ADDITIONNELS REQUIS PAR LES COMPOSANTS
// =============================================

/**
 * Hook pour les statistiques d'usage des tags (par type d'entité)
 */
export interface TagUsageStats {
  total_tags_used: number;
  total_usage: number;
  mostUsed: any[];
  leastUsed: any[];
}

// Résultats typés pour la recherche globale par tags
export interface GlobalSearchResults {
  [entityType: string]: {
    total: number;
    entities: any[];
  };
}

export const useTagUsageStats = (entityType?: string) => {
  return useQuery<TagUsageStats>({
    queryKey: entityTagsQueryKeys.tagStats(entityType || 'all'),
    queryFn: async (): Promise<TagUsageStats> => {
      console.log('📊 Getting tag usage stats', { entityType });
      // Placeholder en attendant l'API réelle
      return { total_tags_used: 0, total_usage: 0, mostUsed: [], leastUsed: [] };
    },
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Hook pour la recherche globale par tags
 * Accepte une liste de noms de tags et retourne un mapping par type d'entité
 */
export const useGlobalSearchByTags = (tags: string[], enabled: boolean = true) => {
  return useQuery<GlobalSearchResults>({
    queryKey: ['global-search-tags', tags],
    queryFn: async (): Promise<GlobalSearchResults> => {
      console.log('🔍 Global search by tags:', tags);
      // Placeholder: structure typée attendue par l'UI
      return {
        contact: { total: 0, entities: [] },
        company: { total: 0, entities: [] },
        deal: { total: 0, entities: [] },
        task: { total: 0, entities: [] },
      };
    },
    enabled: enabled && tags.length > 0,
    staleTime: 2 * 60 * 1000,
  })
}

/**
 * Hook pour ajouter des tags à une entité
 */
export const useAddTagsToEntity = () => {
  const invalidateTagCaches = useInvalidateTagCaches()

  return {
    mutateAsync: async ({ entityType, entityId, tagIds }: {
      entityType: string
      entityId: number
      tagIds: number[]
    }) => {
      console.log('➕ Adding tags to entity:', { entityType, entityId, tagIds });
      
      try {
        // Simuler l'ajout pour l'instant
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Invalider les caches
        invalidateTagCaches(entityType, entityId);
        
        return { success: true };
      } catch (error) {
        console.error('❌ Erreur lors de l\'ajout des tags:', error);
        throw error;
      }
    }
  };
}

/**
 * Hook pour retirer un tag d'une entité
 */
export const useRemoveTagFromEntity = () => {
  const invalidateTagCaches = useInvalidateTagCaches()

  return {
    mutateAsync: async ({ entityType, entityId, tagId }: {
      entityType: string
      entityId: number
      tagId: number
    }) => {
      console.log('➖ Removing tag from entity:', { entityType, entityId, tagId });
      
      try {
        // Simuler la suppression pour l'instant
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Invalider les caches
        invalidateTagCaches(entityType, entityId);
        
        return { success: true };
      } catch (error) {
        console.error('❌ Erreur lors du retrait du tag:', error);
        throw error;
      }
    }
  };
}

/**
 * Hook pour les suggestions de tags
 */
export const useTagSuggestions = (entityType: string, searchTerm: string = '') => {
  return useQuery({
    queryKey: ['tag-suggestions', entityType, searchTerm],
    queryFn: async () => {
      console.log('💡 Getting tag suggestions:', { entityType, searchTerm });
      // Retourner des suggestions vides pour l'instant
      return [];
    },
    enabled: !!entityType,
    staleTime: 1 * 60 * 1000,
  })
}

/**
 * Hook pour invalider les caches après une mutation de tags
 */
export const useInvalidateTagCaches = () => {
  const queryClient = useQueryClient()

  return (entityType: string, entityId?: number) => {
    // Invalider les filtres pour ce type d'entité
    queryClient.invalidateQueries({ queryKey: ['entity-tags', 'filtered-entities', entityType] })
    
    // Si on a un entityId spécifique, l'invalider aussi
    if (entityId) {
      queryClient.invalidateQueries({ queryKey: entityTagsQueryKeys.entity(entityType, entityId) })
    }
    
    // Cross-module invalidation : invalider aussi les caches useTags
    queryClient.invalidateQueries({ queryKey: tagKeys.all })
    queryClient.invalidateQueries({ queryKey: tagKeys.lists() })
    queryClient.invalidateQueries({ queryKey: tagKeys.hierarchy() })
    queryClient.invalidateQueries({ queryKey: tagKeys.summary() })
    
    console.log('✅ Cache invalidation completed (simplified system)')
  }
}
